import React, { useState, useEffect } from 'react';
import api from '../api';
import './PIIRequestModal.css';

const PIIRequestModal = ({ 
  isOpen, 
  profileUsername, 
  profileName, 
  onClose, 
  onSuccess, 
  onRefresh, 
  currentAccess = {}, 
  requestStatus = {}, 
  visibilitySettings = {},
  requesterProfile = null,  // Current user's profile to check if they have data to share
  targetProfile = null  // Target user's profile to check if they have data (e.g., photos)
}) => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const currentUsername = localStorage.getItem('username');

  // Map PII type values to visibility setting keys (for non-image PII types)
  const visibilityKeyMap = {
    'contact_number': 'contactNumberVisible',
    'contact_email': 'contactEmailVisible',
    'linkedin_url': 'linkedinUrlVisible'
    // 'images' removed - now uses imageVisibility.onRequest bucket system
  };
  
  // Check if images need request using 3-bucket visibility system
  // Returns true if ALL photos are already visible (no onRequest photos)
  const areAllImagesVisible = () => {
    if (!targetProfile) return false;
    const imageVisibility = targetProfile.imageVisibility;
    if (imageVisibility) {
      const onRequestPhotos = imageVisibility.onRequest || [];
      return onRequestPhotos.length === 0;
    }
    // No imageVisibility = no private photos (legacy profiles)
    return true;
  };

  // Map PII type values to requester's data fields
  const requesterDataMap = {
    'contact_number': (profile) => profile?.contactNumber || profile?.phone,
    'contact_email': (profile) => profile?.contactEmail || profile?.email,
    'linkedin_url': (profile) => profile?.linkedinUrl || profile?.linkedin,
    'images': (profile) => profile?.images?.length > 0
  };

  // Map PII type values to target's data fields (to check if they have data to share)
  const targetDataMap = {
    'contact_number': (profile) => profile?.contactNumber || profile?.phone,
    'contact_email': (profile) => profile?.contactEmail || profile?.email,
    'linkedin_url': (profile) => profile?.linkedinUrl || profile?.linkedin,
    'images': (profile) => profile?.images?.length > 0
  };

  // Check if requester has data to share for a given PII type
  const requesterHasData = (piiType) => {
    if (!requesterProfile) {
      console.log('⚠️ PIIRequestModal: requesterProfile is null/undefined');
      return true; // If no profile provided, assume they have data
    }
    const checker = requesterDataMap[piiType];
    const hasData = checker ? !!checker(requesterProfile) : true;
    console.log(`🔍 PIIRequestModal: Checking ${piiType} - hasData: ${hasData}`, {
      contactNumber: requesterProfile.contactNumber,
      contactEmail: requesterProfile.contactEmail,
      linkedinUrl: requesterProfile.linkedinUrl,
      imagesCount: requesterProfile.images?.length
    });
    return hasData;
  };

  // Check if target has data to share for a given PII type
  const targetHasData = (piiType) => {
    if (!targetProfile) {
      console.log('⚠️ PIIRequestModal: targetProfile is null/undefined');
      return true; // If no profile provided, assume they have data
    }
    const checker = targetDataMap[piiType];
    const hasData = checker ? !!checker(targetProfile) : true;
    console.log(`🔍 PIIRequestModal: Target has ${piiType}: ${hasData}`, {
      contactNumber: targetProfile.contactNumber,
      contactEmail: targetProfile.contactEmail,
      linkedinUrl: targetProfile.linkedinUrl,
      imagesCount: targetProfile.images?.length
    });
    return hasData;
  };

  // All possible PII types
  const allPiiTypes = [
    { value: 'images', label: '📷 View Photos', description: 'Access to profile pictures' },
    { value: 'contact_number', label: '📞 Contact Number', description: 'Phone number for direct contact' },
    { value: 'contact_email', label: '📧 Contact Email', description: 'Email address for direct contact' },
    { value: 'linkedin_url', label: '🔗 LinkedIn Profile', description: 'LinkedIn profile URL' }
  ];

  // Show ALL types - member-visible ones will be shown as checked+locked
  const piiTypes = allPiiTypes;

  // Get list of member-visible fields for info message (kept for reference)
  // eslint-disable-next-line no-unused-vars
  const memberVisibleFields = allPiiTypes.filter(type => {
    const visibilityKey = visibilityKeyMap[type.value];
    return visibilityKey && visibilitySettings[visibilityKey] === true;
  });

  // ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isOpen, onClose, submitting]);

  // Initialize selected types with already granted access or pending requests
  useEffect(() => {
    if (isOpen) {
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // ❌ REMOVED: Auto-refresh on open causes infinite loop
      // Parent should already have fresh data before opening modal
      // if (onRefresh) {
      //   console.log('🔄 PIIRequestModal opened - triggering status refresh...');
      //   onRefresh();
      // }
      
      console.log('🔍 PIIRequestModal opened for:', profileUsername);
      console.log('📊 Current request status:', requestStatus);
      
      const alreadyGrantedOrPending = piiTypes
        .filter(type => {
          const status = requestStatus[type.value];
          console.log(`  ${type.label}: status = ${status || 'none'}`);
          return status === 'approved' || status === 'pending';
        })
        .map(type => type.label)
        .join(', ');
      
      if (alreadyGrantedOrPending) {
        console.log('✅ Already granted/pending:', alreadyGrantedOrPending);
      }
      
    } else {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = 'unset';
      
      // Reset when modal closes
      setSelectedTypes([]);
      setMessage('');
      setError('');
      setSuccessMessage('');
    }
    
    // Cleanup function to ensure scroll is restored
    return () => {
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onRefresh]);

  const handleToggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleSelectAll = () => {
    // Get all locked types (already approved/pending - can't be deselected)
    const lockedTypes = piiTypes
      .filter(type => {
        const status = requestStatus[type.value];
        return status === 'approved' || status === 'pending';
      })
      .map(type => type.value);
    
    // Get all available types (not locked AND requester has data to share)
    const availableTypes = piiTypes
      .filter(type => {
        const status = requestStatus[type.value];
        const visKey = visibilityKeyMap[type.value];
        const isMemberVis = visKey && visibilitySettings[visKey] === true;
        const canShare = requesterHasData(type.value);
        return status !== 'approved' && status !== 'pending' && !isMemberVis && canShare;
      })
      .map(type => type.value);
    
    // Select all: locked + available
    setSelectedTypes([...lockedTypes, ...availableTypes]);
  };

  const handleDeselectAll = () => {
    // Keep only the approved/pending items (can't be deselected)
    const lockedTypes = piiTypes
      .filter(type => {
        const status = requestStatus[type.value];
        return status === 'approved' || status === 'pending';
      })
      .map(type => type.value);
    
    setSelectedTypes(lockedTypes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out already granted access AND pending requests
    const typesToRequest = selectedTypes.filter(type => {
      const status = requestStatus[type];
      return status !== 'approved' && status !== 'pending';
    });
    
    if (typesToRequest.length === 0) {
      setError('You already have access to all selected information or requests are pending');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await api.post(`/pii-requests?username=${currentUsername}`, {
        profileUsername,
        requestTypes: typesToRequest,
        message: message.trim() || null
      });

      // Call parent success handler FIRST to update status immediately
      if (onSuccess) {
        await onSuccess();
      }
      
      // Paid members get instant approval on contact fields
      const autoApproved = res?.data?.autoApproved || [];
      setSuccessMessage(
        autoApproved.length > 0
          ? `✅ Access granted — contact info unlocked!`
          : `Request sent successfully to ${profileName}!`
      );
      
      // Auto-hide success message and close modal
      setTimeout(() => {
        setSuccessMessage('');
        onClose();
        
        // Reset form
        setSelectedTypes([]);
        setMessage('');
      }, 2000);
    } catch (err) {
      console.error('Error creating PII request:', err);
      // Handle Pydantic validation errors (array of objects with {type, loc, msg, input, url})
      const errorDetail = err.response?.data?.detail;
      let errorMsg = 'Failed to send request';
      
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMsg = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        // Single error object
        errorMsg = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      setError(errorMsg);
      // Auto-hide error after 5 seconds
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('pii-modal-overlay')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pii-modal-overlay" onClick={handleOverlayClick}>
      {/* Error Bubble */}
      {error && (
        <div className="status-bubble error-bubble">
          <span className="bubble-icon">❌</span>
          <div className="bubble-content">
            <strong className="bubble-title">Error</strong>
            <p className="bubble-message">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="bubble-close"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Success Bubble */}
      {successMessage && (
        <div className="status-bubble success-bubble">
          <span className="bubble-icon">✅</span>
          <div className="bubble-content">
            <strong className="bubble-title">Success</strong>
            <p className="bubble-message">{successMessage}</p>
          </div>
        </div>
      )}

      <div className="pii-modal">
        <div className="pii-modal-header">
          <h3>🔒 Request Access to Information</h3>
          <p>Request access to {profileName}'s private information</p>
          <p className="mutual-share-note">Data access is a mutual share -- your matching data will be shared with requestee</p>
          <button className="pii-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="pii-modal-body">
          {/* Scrollable content area */}
          <div className="pii-modal-content">
            <div className="pii-types-section">
              <div className="section-header">
                <label className="section-label">Select information to request:</label>
                <div className="select-all-buttons">
                  <button
                    type="button"
                    className="btn-select-all"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn-deselect-all"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              
              {piiTypes.map(type => {
                const status = requestStatus[type.value]; // 'approved', 'pending', 'expired', or null
                const hasAccess = status === 'approved';
                const isPending = status === 'pending';
                const isExpired = status === 'expired';
                
                // Check if this field is already visible to members (no request needed)
                // BUT also check if target actually HAS the data (e.g., photos exist)
                const visibilityKey = visibilityKeyMap[type.value];
                const targetHasThisData = targetHasData(type.value);
                
                // For images: use new 3-bucket system (check if no onRequest photos)
                // For other fields: use legacy visibility boolean
                let visibilitySetting;
                if (type.value === 'images') {
                  visibilitySetting = areAllImagesVisible();
                } else {
                  visibilitySetting = visibilityKey && visibilitySettings[visibilityKey] === true;
                }
                
                // Only show "Already Member Visible" if BOTH setting is true AND data exists
                const isMemberVisible = visibilitySetting && targetHasThisData;
                
                // Check if requester has this data to share (mutual exchange requirement)
                const canShare = requesterHasData(type.value);
                
                // Disable if: has access, pending, member visible, OR requester can't share
                const isDisabled = hasAccess || isPending || isMemberVisible || !canShare;
                // Member-visible fields should appear checked (but locked)
                // Ensure boolean value to avoid uncontrolled->controlled warning
                const isSelected = selectedTypes.includes(type.value) || isMemberVisible || false;
                
                return (
                  <div
                    key={type.value}
                    className={`pii-type-option ${isSelected ? 'selected' : ''} ${hasAccess ? 'has-access' : ''} ${isPending ? 'pending' : ''} ${isExpired ? 'expired' : ''} ${isMemberVisible ? 'member-visible' : ''} ${!canShare ? 'no-data-to-share' : ''}`}
                    onClick={() => !isDisabled && handleToggleType(type.value)}
                  >
                    <div className="pii-type-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => handleToggleType(type.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="pii-type-info">
                      <div className="pii-type-label">
                        {type.label}
                        {!canShare && <span className="no-share-badge">⚠️ You don't have this to share</span>}
                        {hasAccess && !isMemberVisible && canShare && <span className="access-badge">✅ Already Granted</span>}
                        {isPending && canShare && <span className="pending-badge">📨 Request Sent</span>}
                        {isExpired && canShare && <span className="expired-badge">🔒 Access Expired</span>}
                      </div>
                      {isMemberVisible && canShare && (
                        <div className="member-visible-badge">👁️ Already Member Visible</div>
                      )}
                      {visibilitySetting && !targetHasThisData && canShare && (
                        <div className="no-data-badge">📭 Member has no {type.label.replace(/[📷📞📧🔗]\s*/g, '').toLowerCase()} yet</div>
                      )}
                      <div className="pii-type-description">{type.description}</div>
                      {/* Conditional notes based on requester data and visibility settings */}
                      {!canShare && (
                        <div className="pii-type-note note-warning">
                          You can not make request since you don't have any {type.label.replace(/[📷📞📧🔗]\s*/g, '').toLowerCase()} to share with member, it's a mutual share
                        </div>
                      )}
                      {isMemberVisible && (
                        <div className="pii-type-note note-muted">
                          {type.value === 'images' 
                            ? "All photos are already visible (no private 🔒 On Request photos)"
                            : `No request needed - ${type.label.replace(/[📷📞📧🔗]\s*/g, '')} is visible to all members`
                          }
                        </div>
                      )}
                      {visibilitySetting && !targetHasThisData && canShare && (
                        <div className="pii-type-note note-info">
                          Member has no {type.label.replace(/[📷📞📧🔗]\s*/g, '').toLowerCase()} yet. Your request will notify them to add it.
                        </div>
                      )}
                      {canShare && !isMemberVisible && (
                        <div className="pii-type-note note-success">
                          Your {type.label.replace(/[📷📞📧🔗]\s*/g, '').toLowerCase()} will be shared as well
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pii-message-section">
              <label className="section-label">Optional message:</label>
              <textarea
                className="pii-message-input"
                placeholder="Why do you need this information? (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={3}
              />
              <div className="char-count">{message.length}/500</div>
            </div>
          </div>

          {/* Sticky footer with action buttons */}
          <div className="pii-modal-footer">
            <div className="pii-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting || selectedTypes.filter(t => {
                  const status = requestStatus[t];
                  const visKey = visibilityKeyMap[t];
                  const isMemberVis = visKey && visibilitySettings[visKey] === true;
                  const canShare = requesterHasData(t);
                  return status !== 'approved' && status !== 'pending' && !isMemberVis && canShare;
                }).length === 0}
              >
                {submitting 
                  ? 'Sending...' 
                  : `Send Request (${selectedTypes.filter(t => {
                      const status = requestStatus[t];
                      const visKey = visibilityKeyMap[t];
                      const isMemberVis = visKey && visibilitySettings[visKey] === true;
                      const canShare = requesterHasData(t);
                      return status !== 'approved' && status !== 'pending' && !isMemberVis && canShare;
                    }).length})`
                }
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PIIRequestModal;
